import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { TextField, FormControlLabel, Switch } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreateInventoryItem } from '../../../hooks/inventory/useCreateInventoryItem';
import { useUpdateInventoryItem } from '../../../hooks/inventory/useUpdateInventoryItem';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import CategorySelect from '../controls/CategorySelect';
import SupplierAutocomplete from '../controls/SupplierAutocomplete';
import ActiveRadioGroup from '../../common/controls/ActiveRadioGroup';
import DatePicker from '../../common/controls/DatePicker';
import { isValidDate } from '../../../utils/isValidDate';
import { getInventorySupplierById } from '../../../api/inventoryService';

export default function InventoryItemForm({ successCallback = null, item = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [name, setName] = React.useState(item ? item.name : "");
    const [sku, setSku] = React.useState(item ? item.sku : "");
    const [uom, setUom] = React.useState(item ? item.uom : "");
    const [costPrice, setCostPrice] = React.useState(item ? item.cost_price : 0);
    const [sellingPrice, setSellingPrice] = React.useState(item ? item.selling_price : 0);
    const [categoryId, setCategoryId] = React.useState(item ? item.category_id : "");
    const [supplierId, setSupplierId] = React.useState(item ? item.supplier_id : "");
    const [lowStock, setLowStock] = React.useState(item ? item.low_stock : 0);
    const [expiryDate, setExpiryDate] = React.useState(item ? item.expiry_date : "");
    const [status, setStatus] = React.useState(item ? item.status : "");
    const [autoNotifySupplier, setAutoNotifySupplier] = React.useState(item ? item.auto_notify_supplier : false);
    const [supplierHasEmail, setSupplierHasEmail] = React.useState(false);

    const { create } = useCreateInventoryItem(successCallback);
    const { update } = useUpdateInventoryItem(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        setName(item ? item.name : "");
        setSku(item ? item.sku : "");
        setUom(item ? item.uom : "");
        setCostPrice(item ? item.cost_price : "");
        setSellingPrice(item ? item.selling_price : "");
        setCategoryId(item ? item.category_id : "");
        setSupplierId(item ? item.supplier_id : "");
        setLowStock(item ? item.low_stock : "");
        setExpiryDate(item ? item.expiry_date : "");
        setStatus(item ? item.status : "");
        setAutoNotifySupplier(item ? item.auto_notify_supplier : false);
    }, [item, open]);

    // for disabling auto send notification toggle
    React.useEffect(() => {
        if (open) {
          const fetchData = async () => {
            const res = await getInventorySupplierById(supplierId);
            if (res.email) {
              setSupplierHasEmail(true);
            } else {
              setAutoNotifySupplier(false);
              setSupplierHasEmail(false);
            }
          };
  
          if (supplierId) {
            fetchData();
          } else {
            setAutoNotifySupplier(false);
            setSupplierHasEmail(false);
          }
        }
    }, [supplierId, open]);

    const handleExpiryDateChange = (date) => {
        if (date && date.formatted) {
            setExpiryDate(date.formatted);
        }
    };

    const handleSave = async () => {
        const isValidCurrency = (value) => {
            // Matches numbers like "1", "1.23", ".9", ".50"
            return /^(?:\d+|\d*\.\d{1,2})$/.test(value);
        };

        if (!name.trim()) {
            showSnackbar(t('inventory.items.messages.name_required'), "warning");
            return;
        }
        if (!uom.trim()) {
            showSnackbar(t('inventory.items.messages.uom_required'), "warning");
            return;
        }
        if (!costPrice.trim()) {
            showSnackbar(t('inventory.items.messages.cost_required'), "warning");
            return;
        }
        if (!isValidCurrency(costPrice)) {
            showSnackbar(t('inventory.items.messages.cost_invalid'), "warning");
            return;
        }
        if (!sellingPrice.trim()) {
            showSnackbar(t('inventory.items.messages.selling_price_required'), "warning");
            return;
        }
        if (!isValidCurrency(sellingPrice)) {
            showSnackbar(t('inventory.items.messages.selling_price_invalid'), "warning");
            return;
        }
        if (!isValidCurrency(lowStock)) {
            showSnackbar(t('inventory.items.messages.low_stock_invalid'), "warning");
            return;
        }
        if (!categoryId) {
            showSnackbar(t('inventory.items.messages.category_required'), "warning");
            return;
        }

        try {
            if (mode === "create") {
                await create({ 
                    name: name,
                    sku: sku,
                    uom: uom,
                    cost_price: Number(costPrice),
                    selling_price: Number(sellingPrice),
                    category_id: categoryId,
                    supplier_id: supplierId ? supplierId : null,
                    auto_notify_supplier: autoNotifySupplier,
                    low_stock: lowStock,
                    expiry_date: isValidDate(expiryDate) ? expiryDate : null,
                    status: status ? status : "active"
                });
                showSnackbar(t('inventory.items.messages.create_success'), "success");
            } else if (mode === "edit" && item) {
                await update(item.id, { 
                    name: name,
                    sku: sku,
                    uom: uom,
                    cost_price: Number(costPrice),
                    selling_price: Number(sellingPrice),
                    category_id: categoryId,
                    supplier_id: supplierId ? supplierId : null,
                    auto_notify_supplier: autoNotifySupplier,
                    low_stock: lowStock,
                    expiry_date: isValidDate(expiryDate) ? expiryDate : null,
                    status: status ? status : "active"
                });
                showSnackbar(t('inventory.items.messages.update_success'), "success");
            }
            setName("");
            setSku("");
            setUom("");
            setCostPrice("");
            setSellingPrice("");
            setCategoryId("");
            setSupplierId("");
            setStatus("");
            setLowStock("");
            setExpiryDate("");
            setAutoNotifySupplier(false);
            setSupplierHasEmail(false);
            onClose();     // close dialog
        } catch (err) {
            console.log("err.response", err.response);
            showSnackbar(t('inventory.items.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
        setName("");
        setSku("");
        setUom("");
        setCostPrice("");
        setSellingPrice("");
        setCategoryId("");
        setSupplierId("");
        setStatus("");
        setLowStock("");
        setExpiryDate("");
        setAutoNotifySupplier(false);
        setSupplierHasEmail(false);
        onClose();
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Add Item" : "Edit Item"}</DialogTitle>

        <DialogContent dividers>
            <Stack spacing={1}>
            <CategorySelect
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)} 
            />
            <TextField
                label="Item Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                fullWidth
            />
            <TextField
                label="SKU (optional)"
                variant="outlined"
                value={sku}
                onChange={(e) => setSku(e.target.value)}
                fullWidth
            />
            <TextField
                label="Unit of Measure (UoM)"
                variant="outlined"
                value={uom}
                onChange={(e) => setUom(e.target.value)}
                fullWidth
            />
            <TextField
                label="Cost per UoM"
                variant="outlined"
                value={costPrice}
                onChange={(e) => setCostPrice(e.target.value)}
                slotProps={{
                    input: {
                        inputMode: 'decimal',
                        pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                    },
                }}
                fullWidth
            />
            <TextField
                label="Selling Price per UoM"
                variant="outlined"
                value={sellingPrice}
                onChange={(e) => setSellingPrice(e.target.value)}
                slotProps={{
                    input: {
                        inputMode: 'decimal',
                        pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                    },
                }}
                fullWidth
            />
            <TextField
                label="Low Stock Amount in UoM"
                variant="outlined"
                value={lowStock}
                onChange={(e) => setLowStock(e.target.value)}
                slotProps={{
                    input: {
                        inputMode: 'decimal',
                        pattern: '^[0-9]+(\\.[0-9]{0,2})?$',
                    },
                }}
                fullWidth
            />
            <SupplierAutocomplete
                label="Supplier (optional)"
                value={supplierId}
                onChange={(id) => {
                  setSupplierId(id);
                }} 
            />
            <FormControlLabel
                control={
                    <Switch
                      disabled={!supplierHasEmail}
                      checked={autoNotifySupplier}
                      onChange={(e) => setAutoNotifySupplier(e.target.checked)}
                      color="primary"
                    />
                }
                label="Auto Notify Supplier On Low Stock"
            />
            <DatePicker 
                defaultValue={expiryDate || ""} 
                onChange={handleExpiryDateChange} 
                label="Expiry Date (optional)"
                minYear={new Date().getFullYear() - 3}
                maxYear={new Date().getFullYear() + 3}
                monthFormat="short"
                size="small" 
                order={["year", "month", "day"]}
                gap={0.5}
                controlWidths={{ month: 2, day: 2, year: 8 }}
                placeholders={{ month: "Month", day: "Day", year: "Year" }}
            />
            {mode === 'edit' ?
            <ActiveRadioGroup 
                value={status} 
                onChange={(e) => setStatus(e.target.value)}
            />
            : null }
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button onClick={handleSave}>
                Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}